import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  VStack,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import Navbar from "../components/Navbar";
import { UserAuth } from "../contexts/AuthContext";
import axios from "axios";
import { apiUrl } from "../config";
import { useToast } from "@chakra-ui/react";

const EMAIL_REGEX = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g;
const EMAIL_VALIDATE_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

const extractEmails = (text) => {
  if (!text) return [];
  const matches = text.match(EMAIL_REGEX) || [];
  const seen = new Set();
  const result = [];
  for (const raw of matches) {
    const normalized = raw.toLowerCase();
    if (!seen.has(normalized)) {
      seen.add(normalized);
      result.push(normalized);
    }
  }
  return result;
};

// Minimal RFC4180-ish CSV parser: handles quoted fields, escaped quotes ("")
// inside quotes, embedded commas/newlines inside quotes, and CRLF or LF line
// endings. Returns an array of row arrays.
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  // Strip UTF-8 BOM if present.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ",") {
      row.push(field);
      field = "";
      i++;
      continue;
    }
    if (ch === "\r") {
      // Skip; \n handles end-of-line.
      i++;
      continue;
    }
    if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  // Flush any trailing field/row.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop fully empty rows.
  return rows.filter((r) => r.some((c) => c && c.trim() !== ""));
};

// Identify which column index in the header row maps to email/business/contact.
// Matches a variety of common header spellings.
const detectColumns = (headerRow) => {
  const normalized = headerRow.map((h) => (h || "").trim().toLowerCase());
  const findIdx = (predicates) =>
    normalized.findIndex((h) => predicates.some((p) => p(h)));

  const emailIdx = findIdx([
    (h) => h === "email",
    (h) => h === "e-mail",
    (h) => h === "email address",
    (h) => h.includes("email"),
  ]);
  const businessIdx = findIdx([
    (h) => h === "business name",
    (h) => h === "business",
    (h) => h === "company",
    (h) => h === "company name",
    (h) => h === "organization",
    (h) => h === "organisation",
    (h) => h.includes("business") && h.includes("name"),
    (h) => h.includes("company") && h.includes("name"),
    (h) => h === "name" || h.endsWith(" name"),
  ]);
  const contactIdx = findIdx([
    (h) => h === "contact name",
    (h) => h === "contact",
    (h) => h === "primary contact",
    (h) => h === "owner",
    (h) => h === "first name",
    (h) => h.includes("contact") && h.includes("name"),
    (h) => h.includes("contact"),
  ]);
  // If "name" matched as the business column, don't also treat it as contact.
  return {
    emailIdx,
    businessIdx,
    contactIdx: contactIdx === businessIdx ? -1 : contactIdx,
  };
};

const parseCsvToRows = (text) => {
  const raw = parseCsv(text);
  if (raw.length === 0) {
    return { rows: [], error: "CSV is empty." };
  }
  const { emailIdx, businessIdx, contactIdx } = detectColumns(raw[0]);
  if (emailIdx === -1) {
    return {
      rows: [],
      error:
        "Could not find an email column. Make sure your CSV has a header row with a column named 'email'.",
    };
  }
  const dataRows = raw.slice(1);
  const seen = new Set();
  const rows = [];
  dataRows.forEach((cols) => {
    const email = (cols[emailIdx] || "").trim().toLowerCase();
    if (!email || !EMAIL_VALIDATE_REGEX.test(email)) return;
    if (seen.has(email)) return;
    seen.add(email);
    rows.push({
      email,
      businessName:
        businessIdx >= 0 ? (cols[businessIdx] || "").trim() : "",
      contactName:
        contactIdx >= 0 ? (cols[contactIdx] || "").trim() : "",
    });
  });
  return { rows, error: null };
};

const BusinessTypeSelection = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const { user } = UserAuth();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [pastedText, setPastedText] = useState("");
  const [csvRows, setCsvRows] = useState([]);
  const [csvError, setCsvError] = useState(null);
  const [csvFileName, setCsvFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const detectedEmails = useMemo(() => extractEmails(pastedText), [pastedText]);

  // The active list of invitees depends on which tab the user is on.
  const inviteeRows = useMemo(() => {
    if (tabIndex === 1) return csvRows;
    return detectedEmails.map((email) => ({
      email,
      businessName: "",
      contactName: "",
    }));
  }, [tabIndex, csvRows, detectedEmails]);

  const handleSelection = (isSelfEntry) => {
    if (isSelfEntry) {
      navigate(`/add-business?campaignId=${campaignId}`);
    } else {
      setIsEmailModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsEmailModalOpen(false);
    setPastedText("");
    setCsvRows([]);
    setCsvError(null);
    setCsvFileName("");
    setTabIndex(0);
    setSubmissionResult(null);
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result || "";
      const { rows, error } = parseCsvToRows(String(text));
      setCsvRows(rows);
      setCsvError(error);
    };
    reader.onerror = () => {
      setCsvRows([]);
      setCsvError("Could not read the file.");
    };
    reader.readAsText(file);
  };

  const removeCsvRow = (email) => {
    setCsvRows((prev) => prev.filter((r) => r.email !== email));
  };

  const handleSendInvitations = async () => {
    if (inviteeRows.length === 0 || isSubmitting) return;
    setIsSubmitting(true);

    const idToken = sessionStorage.getItem("idToken");

    const results = await Promise.allSettled(
      inviteeRows.map((row) =>
        axios.post(
          `${apiUrl}/generate-business-link`,
          {
            email: row.email,
            businessName: row.businessName || undefined,
            contactName: row.contactName || undefined,
            campaignId,
            type: "new",
            userId: user.uid,
          },
          {
            headers: { Authorization: `Bearer ${idToken}` },
          },
        ),
      ),
    );

    const succeeded = [];
    const failed = [];
    results.forEach((result, idx) => {
      const row = inviteeRows[idx];
      if (result.status === "fulfilled") {
        succeeded.push({
          email: row.email,
          url: result.value?.data?.businessUrl,
        });
      } else {
        failed.push({
          email: row.email,
          error:
            result.reason?.response?.data?.message ||
            result.reason?.response?.data?.error ||
            result.reason?.message ||
            "Unknown error",
        });
      }
    });

    setSubmissionResult({ succeeded, failed });
    setIsSubmitting(false);

    if (failed.length === 0) {
      toast({
        title: "Invitations sent",
        description: `Sent ${succeeded.length} invitation${
          succeeded.length === 1 ? "" : "s"
        } successfully.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else if (succeeded.length === 0) {
      toast({
        title: "Failed to send invitations",
        description: "No invitations were sent. See details below.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Some invitations failed",
        description: `${succeeded.length} sent, ${failed.length} failed.`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const retryFailed = () => {
    if (!submissionResult) return;
    const failedEmails = new Set(submissionResult.failed.map((f) => f.email));
    if (tabIndex === 1) {
      setCsvRows((prev) => prev.filter((r) => failedEmails.has(r.email)));
    } else {
      setPastedText(submissionResult.failed.map((f) => f.email).join("\n"));
    }
    setSubmissionResult(null);
  };

  const sendButtonLabel =
    inviteeRows.length <= 1
      ? "Send Invitation"
      : `Send ${inviteeRows.length} Invitations`;

  return (
    <>
      <Navbar />
      <Box minH="calc(100vh - 60px)" bg="primaryBackground" pt="60px" px={4}>
        <VStack
          align="center"
          maxWidth="800px"
          margin="0 auto"
          pt={8}
          spacing={8}
        >
          <Heading as="h1" size="lg" textAlign="center" color="heading">
            Add a Business
          </Heading>

          <Box
            width="100%"
            bg="secondaryBackground"
            p={8}
            borderRadius="xl"
            boxShadow="md"
          >
            <VStack spacing={6} width="100%">
              <Text textAlign="center" fontSize="lg">
                Choose how to add the business.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="100%">
                <Card
                  as="button"
                  onClick={() => handleSelection(true)}
                  cursor="pointer"
                  bg="white"
                  borderColor="gray.200"
                  borderWidth="2px"
                  _hover={{ bg: "#f8ffec", borderColor: "primaryHighlight" }}
                >
                  <CardBody>
                    <VStack spacing={3}>
                      <Text fontSize="xl" fontWeight="bold">
                        Enter Details Now
                      </Text>
                      <Text color="gray.600">
                        I have all the business information and will enter it
                        myself.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
                <Card
                  as="button"
                  onClick={() => handleSelection(false)}
                  cursor="pointer"
                  bg="white"
                  borderColor="gray.200"
                  borderWidth="2px"
                  _hover={{ bg: "#f8ffec", borderColor: "primaryHighlight" }}
                >
                  <CardBody>
                    <VStack spacing={3}>
                      <Text fontSize="xl" fontWeight="bold">
                        Send to Business
                      </Text>
                      <Text color="gray.600">
                        Send a link to one or more businesses to submit their
                        information.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </VStack>
          </Box>
        </VStack>
      </Box>

      <Modal isOpen={isEmailModalOpen} onClose={closeModal} size="2xl">
        <ModalOverlay />
        <ModalContent>
          {!submissionResult ? (
            <>
              <ModalHeader>Send Invitations to Businesses</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Tabs
                  index={tabIndex}
                  onChange={setTabIndex}
                  variant="enclosed"
                  colorScheme="green"
                >
                  <TabList>
                    <Tab>Paste Emails</Tab>
                    <Tab>Upload CSV</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                          <FormLabel>Business email addresses</FormLabel>
                          <Textarea
                            value={pastedText}
                            onChange={(e) => setPastedText(e.target.value)}
                            placeholder={
                              "Paste any text containing email addresses — one per line, comma-separated, copied from a spreadsheet, or mixed in with other text. Anything that isn't an email address is ignored."
                            }
                            rows={8}
                          />
                        </FormControl>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            {detectedEmails.length === 0
                              ? "No email addresses detected yet."
                              : `${detectedEmails.length} email address${
                                  detectedEmails.length === 1 ? "" : "es"
                                } detected.`}
                          </Text>
                        </HStack>
                        {detectedEmails.length > 0 && (
                          <Box
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            p={2}
                            maxH="160px"
                            overflowY="auto"
                          >
                            <Wrap>
                              {detectedEmails.map((email) => (
                                <WrapItem key={email}>
                                  <Tag
                                    size="sm"
                                    colorScheme="green"
                                    variant="subtle"
                                  >
                                    <TagLabel>{email}</TagLabel>
                                  </Tag>
                                </WrapItem>
                              ))}
                            </Wrap>
                          </Box>
                        )}
                      </VStack>
                    </TabPanel>
                    <TabPanel px={0}>
                      <VStack spacing={4} align="stretch">
                        <Text fontSize="sm" color="gray.600">
                          Upload a CSV with a header row. The first column whose
                          header contains <b>email</b> is required; columns for{" "}
                          <b>business name</b> and <b>contact name</b> will be
                          matched automatically when present.
                        </Text>
                        <HStack>
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            variant="outline"
                          >
                            {csvFileName ? "Choose Different File" : "Choose CSV File"}
                          </Button>
                          {csvFileName && (
                            <Text fontSize="sm" color="gray.700">
                              {csvFileName}
                            </Text>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              handleCsvFile(file);
                              // Reset so picking the same file again re-triggers onChange.
                              e.target.value = "";
                            }}
                          />
                        </HStack>
                        {csvError && (
                          <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="sm">{csvError}</Text>
                          </Alert>
                        )}
                        {csvRows.length > 0 && (
                          <>
                            <Text fontSize="sm" color="gray.600">
                              Preview — {csvRows.length} business
                              {csvRows.length === 1 ? "" : "es"} ready to invite.
                              Review the list and remove any rows you don't want
                              to send.
                            </Text>
                            <Box
                              borderWidth="1px"
                              borderColor="gray.200"
                              borderRadius="md"
                              maxH="280px"
                              overflowY="auto"
                            >
                              <Table size="sm" variant="simple">
                                <Thead bg="gray.50" position="sticky" top={0}>
                                  <Tr>
                                    <Th>Business Name</Th>
                                    <Th>Contact Name</Th>
                                    <Th>Email</Th>
                                    <Th width="40px"></Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {csvRows.map((row) => (
                                    <Tr key={row.email}>
                                      <Td>
                                        {row.businessName || (
                                          <Text as="span" color="gray.400">
                                            —
                                          </Text>
                                        )}
                                      </Td>
                                      <Td>
                                        {row.contactName || (
                                          <Text as="span" color="gray.400">
                                            —
                                          </Text>
                                        )}
                                      </Td>
                                      <Td>{row.email}</Td>
                                      <Td>
                                        <IconButton
                                          aria-label={`Remove ${row.email}`}
                                          icon={<CloseIcon boxSize={2.5} />}
                                          size="xs"
                                          variant="ghost"
                                          onClick={() => removeCsvRow(row.email)}
                                        />
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                          </>
                        )}
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSendInvitations}
                  isLoading={isSubmitting}
                  isDisabled={inviteeRows.length === 0}
                >
                  {sendButtonLabel}
                </Button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalHeader>
                {submissionResult.failed.length === 0
                  ? "Invitations sent"
                  : submissionResult.succeeded.length === 0
                    ? "Failed to send invitations"
                    : "Some invitations failed"}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={4}>
                  <Text>
                    Sent: <b>{submissionResult.succeeded.length}</b>
                    {submissionResult.failed.length > 0 && (
                      <>
                        {" • "}Failed:{" "}
                        <b>{submissionResult.failed.length}</b>
                      </>
                    )}
                  </Text>
                  {submissionResult.failed.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={1}>
                        Failures
                      </Text>
                      <Box
                        borderWidth="1px"
                        borderColor="red.200"
                        borderRadius="md"
                        p={2}
                        maxH="180px"
                        overflowY="auto"
                      >
                        <VStack align="stretch" spacing={1}>
                          {submissionResult.failed.map((f) => (
                            <Text key={f.email} fontSize="sm">
                              <b>{f.email}</b> — {f.error}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                {submissionResult.failed.length > 0 && (
                  <Button variant="ghost" mr={3} onClick={retryFailed}>
                    Retry Failed
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    closeModal();
                    navigate(`/campaign/${campaignId}`);
                  }}
                >
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default BusinessTypeSelection;
